export enum Directory {
  /**
   * The Documents directory.
   * On iOS it's the app's documents directory.
   * Use this directory to store user-generated content.
   * On Android it's the Public Documents folder, so it's accessible from other apps.
   * It's not accessible on Android 10 unless the app enables legacy External Storage
   * by adding `android:requestLegacyExternalStorage="true"` in the `application` tag
   * in the `AndroidManifest.xml`.
   * On Android 11 or newer the app can only access the files/folders the app created.
   *
   * @since 1.0.0
   */
  Documents = "DOCUMENTS",

  /**
   * The Data directory.
   * On iOS it will use the Documents directory.
   * On Android it's the directory holding application files.
   * Files will be deleted when the application is uninstalled.
   *
   * @since 1.0.0
   */
  Data = "DATA",

  /**
   * The Library directory.
   * On iOS it will use the Library directory.
   * On Android it's the directory holding application files.
   * Files will be deleted when the application is uninstalled.
   *
   * @since 1.1.0
   */
  Library = "LIBRARY",

  /**
   * The Cache directory.
   * Can be deleted in cases of low memory, so use this directory to write app-specific files.
   * that your app can re-create easily.
   *
   * @since 1.0.0
   */
  Cache = "CACHE",

  /**
   * The external directory.
   * On iOS it will use the Documents directory.
   * On Android it's the directory on the primary shared/external
   * storage device where the application can place persistent files it owns.
   * These files are internal to the applications, and not typically visible
   * to the user as media.
   * Files will be deleted when the application is uninstalled.
   *
   * @since 1.0.0
   */
  External = "EXTERNAL",

  /**
   * The external storage directory.
   * On iOS it will use the Documents directory.
   * On Android it's the primary shared/external storage directory.
   * It's not accessible on Android 10 unless the app enables legacy External Storage
   * by adding `android:requestLegacyExternalStorage="true"` in the `application` tag
   * in the `AndroidManifest.xml`.
   * It's not accessible on Android 11 or newer.
   *
   * @since 1.0.0
   */
  ExternalStorage = "EXTERNAL_STORAGE",
  /**
   * The external cache directory.
   * On iOS it will use the Documents directory.
   * On Android it's the primary shared/external cache.
   *
   * @since 7.1.0
   */
  ExternalCache = "EXTERNAL_CACHE",

  /**
   * The Library directory without cloud backup. Used in iOS.
   * On Android it's the directory holding application files.
   *
   * @since 7.1.0
   */
  LibraryNoCloud = "LIBRARY_NO_CLOUD",

  /**
   * A temporary directory for iOS.
   * On Android it's the directory holding the application cache.
   *
   * @since 7.1.0
   */
  Temporary = "TEMPORARY"
}
/** Http headers shape used by options. */

export interface HttpHeaders {
  [key: string]: string;
}
/** URL query params shape used by options. */

export interface HttpParams {
  [key: string]: string | number | boolean;
}
/** Progress event payload. */

export interface ProgressStatus {
  /** The type of transfer operation (download or upload). */
  type: "download" | "upload";
  /** The url associated with the transfer. */
  url: string;
  /** Bytes transferred so far. */
  bytes: number;
  /** Total number of bytes if known. */
  contentLength: number;
  /** Whether contentLength is known. */
  lengthComputable: boolean;
}
/** Download options (subset + documented fields). */

export interface DownloadFileOptions {
  /** The URL to download the file from. */
  url: string;
  /**
   * Full destination file path (native: file URI).
   * Use @capacitor/filesystem.getUri() to build a full path.
   */
  path: string;
  /** Emit progress events? (native throttled every ~100ms) */
  progress?: boolean;
  /** HTTP method (default: GET) */
  method?: string;
  /** Query string parameters */
  params?: HttpParams;
  /** Request headers (e.g., Authorization) */
  headers?: HttpHeaders;
  /** Read timeout (ms). Not supported on web. */
  readTimeout?: number;
  /** Connect timeout (ms). iOS conflates with read. Not supported on web. */
  connectTimeout?: number;
  /** Disable automatic redirects. Not supported on web. */
  disableRedirects?: boolean;
  /**
   * Whether to URL-encode params automatically (default: true).
   * Not supported on web.
   */
  shouldEncodeUrlParams?: boolean;
}
/** Download result payload. */

export interface DownloadFileResult {
  path: string;
}

export interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

/**
 * The target in which to load the URL
 */
export enum AppBrowserTarget {
    /** Opens in the Cordova WebView if the URL is in the white list, otherwise it opens in the InAppBrowser. */
    self = "_self",
    /** opens in the InAppBrowser */
    blank = "_blank",
    /** opens in the system's web browser */
    system = "_system"
}

/**
 * Scan result returned to app code.
 */
export interface CapacitorBarcodeScannerScanResult {
  ScanResult: string;
  format: CapacitorBarcodeScannerTypeHint;
}

export enum Html5QrcodeSupportedFormats {
    QR_CODE	= 0,
    AZTEC = 1,
    CODABAR = 2,
    CODE_39 = 3,
    CODE_93	= 4,
    CODE_128 = 5,
    DATA_MATRIX = 6,
    MAXICODE = 7,
    ITF = 8,
    EAN_13 = 9,
    EAN_8 = 10,
    PDF_417 = 11,
    RSS_14 = 12,
    RSS_EXPANDED = 13,
    UPC_A = 14,
    UPC_E = 15,
    UPC_EAN_EXTENSION = 16
}

/**
 * Enum representing a special option to indicate that all barcode types are supported.
 */
export enum CapacitorBarcodeScannerTypeHintALLOption {
  ALL = 17
}

export type CapacitorBarcodeScannerTypeHint =
  | Html5QrcodeSupportedFormats
  | CapacitorBarcodeScannerTypeHintALLOption;

/**
 * Enum representing the orientation of the scanner during barcode scanning.
 */
export enum CapacitorBarcodeScannerScanOrientation {
  PORTRAIT = 1,
  LANDSCAPE = 2,
  ADAPTIVE = 3,
}

/**
 * Enum representing the library to be used for barcode scanning on Android devices.
 */
export enum CapacitorBarcodeScannerAndroidScanningLibrary {
  ZXING = "zxing",
  MLKIT = "mlkit",
}

export enum CapacitorBarcodeScannerCameraDirection {
    BACK = 1,
    FRONT = 2,
}

export interface ConfirmOptions {
  message: string;
  title?: string;
  buttonLabels: string[];
}

export interface AlertOptions {
  message: string;
  title?: string;
  buttonName?: string;
}

export type ConfirmCallback = (buttonIndex: number) => void;
export type AlertCallback = () => void;

export enum BiometryType {
    None = "none",
    TouchId = "touch",
    FaceId = "face",
    Finger = "finger",
    Biometric = "biometric"
}

export enum PluginError {
    BIOMETRIC_UNKNOWN_ERROR = -100,
    BIOMETRIC_UNAVAILABLE = -101,
    BIOMETRIC_AUTHENTICATION_FAILED = -102,
    BIOMETRIC_SDK_NOT_SUPPORTED = -103,
    BIOMETRIC_HARDWARE_NOT_SUPPORTED = -104,
    BIOMETRIC_PERMISSION_NOT_GRANTED = -105,
    BIOMETRIC_NOT_ENROLLED = -106,
    // When fingerprints are not enrolled, Samsung Galaxy S series returned an error code 11.
    BIOMETRIC_NOT_ENROLLED_II = 11,
    BIOMETRIC_INTERNAL_PLUGIN_ERROR = -107,
    BIOMETRIC_DISMISSED = -108,
    BIOMETRIC_PIN_OR_PATTERN_DISMISSED = -109,
    BIOMETRIC_SCREEN_GUARD_UNSECURED = -110,
    BIOMETRIC_LOCKED_OUT = -111,
    BIOMETRIC_LOCKED_OUT_PERMANENT = -112,
    BIOMETRIC_NO_SECRET_FOUND = -113,
}

export interface RegisterBiometricOptions {
  title: string;
  subtitle?: string;
  description: string;
  fallbackButtonTitle?: string;
  cancelButtonTitle?: string;
  confirmationRequired?: boolean;
  secret: string;
  disableBackup?: boolean;
  invalidateOnEnrollment?: boolean;
}